/**
 * PortfolioBoardPane — allocation buckets, drift, and theses.
 *
 * Reads current positions from the portfolio endpoint and lists open
 * theses from the Engineer surface. Offers a "dry-run rebalance" button
 * that calls propose_rebalance via the portfolio_manager A2A skill;
 * the result renders as a list of proposed orders (each Playbook-validated).
 * Never places orders itself.
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { useFocal } from '../lib/focal';

type ProposedOrder = {
  symbol: string;
  side: 'buy' | 'sell';
  qty: number;
  est_usd: number;
  bucket: string;
  reason: string;
  verdict: string;
  verdict_reason: string;
};

type RebalanceProposal = {
  orders: ProposedOrder[];
  total_usd: number;
  total_playbook_rejections: number;
  reason: string;
};

export function PortfolioBoardPane() {
  const { set } = useFocal();
  const [positions, setPositions] = useState<api.PositionPublic[]>([]);
  const [ledgers, setLedgers] = useState<api.DualLedgerAccount | null>(null);
  const [theses, setTheses] = useState<api.EngineerThesis[]>([]);
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState<RebalanceProposal | null>(null);
  const [proposing, setProposing] = useState(false);
  const [proposalErr, setProposalErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [l, pos, th] = await Promise.all([
          api.portfolio.ledgers().catch(() => null),
          api.portfolio.positions('combined').catch(() => [] as api.PositionPublic[]),
          api.engineer.listTheses().catch(() => ({ theses: [] })),
        ]);
        if (!alive) return;
        setLedgers(l);
        setPositions(pos);
        setTheses(th.theses ?? []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function dryRun() {
    setProposing(true);
    setProposalErr(null);
    try {
      // Build inputs: positions already in wire format; targets use a
      // sensible default 60/20/15/5 across core/satellite/safe/cash.
      const positionsArg = positions.map((p) => ({
        symbol: p.symbol,
        qty: p.quantity,
        avg_cost: p.avg_price,
        last_price: p.market_price,
        bucket: 'satellite',
      }));
      const targetsArg = [
        { bucket: 'core', target_pct: 0.60, band_lower_pct: 0.55, band_upper_pct: 0.65 },
        { bucket: 'satellite', target_pct: 0.20, band_lower_pct: 0.15, band_upper_pct: 0.25 },
        { bucket: 'safe', target_pct: 0.15, band_lower_pct: 0.10, band_upper_pct: 0.20 },
        { bucket: 'cash', target_pct: 0.05, band_lower_pct: 0.00, band_upper_pct: 0.10 },
        { bucket: 'hedge', target_pct: 0.00, band_lower_pct: 0.00, band_upper_pct: 0.05 },
      ];
      // v0.5.5: cash_usd comes from the LIVE Schwab cash when available,
      // else SIM cash. We don't combine them — rebalancing across ledgers
      // would be nonsensical (the proposer can't move money between
      // Schwab and SIM).
      const cashUsd =
        ledgers?.live?.available && ledgers.live.cash != null
          ? Number(ledgers.live.cash)
          : Number(ledgers?.sim?.cash ?? 0);
      const result = (await api.panes.rpc('portfolio_manager', 'propose_rebalance', {
        positions: positionsArg,
        targets: targetsArg,
        cash_usd: cashUsd,
        reason: 'drift',
        tolerance_pct: 0.01,
      })) as { result?: RebalanceProposal } | RebalanceProposal;
      // A2A RPC wraps under .result in jsonrpc envelope
      const rebalance = 'result' in (result as Record<string, unknown>)
        ? (result as { result: RebalanceProposal }).result
        : (result as RebalanceProposal);
      setProposal(rebalance);
    } catch (e) {
      setProposalErr(e instanceof Error ? e.message : String(e));
    } finally {
      setProposing(false);
    }
  }

  if (loading) {
    return <div className="text-sm text-ink-mute">loading portfolio…</div>;
  }

  const fmtUsd = (v: number | null | undefined) =>
    v == null ? '—' : `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const combined = ledgers?.combined_nav ?? null;
  const liveNav = ledgers?.live?.available ? (ledgers.live.nav ?? null) : null;
  const simNav = ledgers?.sim?.nav ?? null;
  const noLedger = !ledgers && positions.length === 0;

  return (
    <div className="space-y-4">
      {noLedger && (
        <div className="card p-3 border-warn bg-bg-elev">
          <div className="text-sm font-semibold text-warn">Account data unavailable</div>
          <div className="text-[11px] text-ink-mute mt-0.5">
            Connect Schwab in <a href="/settings?tab=brokerage" className="underline">Settings</a>{' '}
            or wait for the connection to recover.
          </div>
        </div>
      )}
      {/* v0.5.5 dual-ledger NAV strip: total + LIVE + SIM */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="board-nav-strip">
        <div className="card p-3">
          <div className="text-[10px] text-ink-mute uppercase">Total NAV</div>
          <div className="num text-2xl font-bold">{fmtUsd(combined)}</div>
          <div className="text-[10px] text-ink-mute">SIM + LIVE</div>
        </div>
        <div className="card p-3 border-l-2 border-l-ok">
          <div className="text-[10px] text-ink-mute uppercase">LIVE · Schwab</div>
          <div className="num text-2xl font-bold">{fmtUsd(liveNav)}</div>
          <div className="text-[10px] text-ink-mute">
            {ledgers?.live?.available ? 'Real account' : (ledgers?.live?.error ?? 'No Schwab')}
          </div>
        </div>
        <div className="card p-3 border-l-2 border-l-edge">
          <div className="text-[10px] text-ink-mute uppercase">Paper · SIM</div>
          <div className="num text-2xl font-bold">{fmtUsd(simNav)}</div>
          <div className="text-[10px] text-ink-mute">Persisted SIM ledger</div>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          onClick={dryRun}
          disabled={proposing}
          className="btn"
          title="generate a Playbook-gated rebalance proposal (does not place orders)"
        >
          {proposing ? 'computing…' : 'propose rebalance (dry-run)'}
        </button>
      </div>

      {/* Positions */}
      <div className="card p-3">
        <div className="text-xs text-ink-mute mb-2">Positions ({positions.length})</div>
        {positions.length === 0 ? (
          <div className="text-xs text-ink-mute">
            No positions yet. Run{' '}
            <code className="num">meeri bootstrap-import</code> on the server
            to seed from Schwab.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-ink-mute text-left">
              <tr>
                <th className="pb-1">symbol</th>
                <th className="pb-1">mode</th>
                <th className="pb-1 text-right">qty</th>
                <th className="pb-1 text-right">avg</th>
                <th className="pb-1 text-right">last</th>
                <th className="pb-1 text-right">mv</th>
                <th className="pb-1 text-right">p&l</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => {
                const mv = p.market_value ?? p.quantity * p.market_price;
                const cost = p.quantity * p.avg_price;
                const pnl = mv - cost;
                const mode = p.mode ?? 'sim';
                return (
                  <tr
                    key={`${mode}:${p.symbol}`}
                    className="border-t border-edge cursor-pointer hover:bg-bg-elev"
                    onClick={() => set({ kind: 'chart', symbol: p.symbol })}
                  >
                    <td className="py-1 font-semibold">{p.symbol}</td>
                    <td className="py-1">
                      <span
                        className={
                          mode === 'live'
                            ? 'inline-block text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-ok/15 text-ok'
                            : 'inline-block text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-edge text-ink-mute'
                        }
                        data-testid={`board-mode-${mode}-${p.symbol}`}
                      >
                        {mode === 'live' ? 'live' : 'paper'}
                      </span>
                    </td>
                    <td className="py-1 text-right num">{p.quantity}</td>
                    <td className="py-1 text-right num">${p.avg_price.toFixed(2)}</td>
                    <td className="py-1 text-right num">${p.market_price.toFixed(2)}</td>
                    <td className="py-1 text-right num">
                      ${mv.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className={`py-1 text-right num ${pnl >= 0 ? 'text-accent' : 'text-warn'}`}>
                      {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Active theses */}
      <div className="card p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-ink-mute">
            Active theses ({theses.filter((t) => t.status === 'open').length})
          </div>
          <button
            onClick={() => set({ kind: 'thesis_wizard' })}
            className="btn text-xs"
          >
            + new thesis
          </button>
        </div>
        {theses.length === 0 ? (
          <div className="text-xs text-ink-mute">
            No theses yet. Use the wizard to design your first one.
          </div>
        ) : (
          <div className="space-y-1">
            {theses.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between text-xs border-b border-edge py-1"
              >
                <div>
                  <span className="font-semibold">{t.symbol ?? '(multi)'}</span>
                  <span className="text-ink-mute ml-2">{t.archetype}</span>
                  <span className="text-ink-mute ml-2">
                    · {t.positions.length} pos
                  </span>
                </div>
                <span
                  className={`pill text-[10px] ${
                    t.status === 'open' ? '' : 'text-ink-mute'
                  }`}
                >
                  {t.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Proposal results */}
      {proposalErr && (
        <div className="card p-3 border-warn text-xs">{proposalErr}</div>
      )}
      {proposal && <ProposalResult proposal={proposal} />}
    </div>
  );
}

function ProposalResult({ proposal }: { proposal: RebalanceProposal }) {
  return (
    <div className="card p-3 border-accent">
      <div className="text-xs text-ink-mute mb-2">
        Proposed rebalance · <span className="num">{proposal.reason}</span> · dry-run
        (no orders placed)
      </div>
      {proposal.orders.length === 0 ? (
        <div className="text-xs text-ink-mute">
          Nothing to do — portfolio within all target bands.
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-ink-mute text-left">
            <tr>
              <th className="pb-1">side</th>
              <th className="pb-1">symbol</th>
              <th className="pb-1 text-right">qty</th>
              <th className="pb-1 text-right">est $</th>
              <th className="pb-1">bucket</th>
              <th className="pb-1">verdict</th>
            </tr>
          </thead>
          <tbody>
            {proposal.orders.map((o, i) => (
              <tr key={i} className="border-t border-edge">
                <td
                  className={`py-1 font-semibold ${
                    o.side === 'buy' ? 'text-accent' : 'text-warn'
                  }`}
                >
                  {o.side}
                </td>
                <td className="py-1">{o.symbol}</td>
                <td className="py-1 text-right num">{o.qty.toFixed(2)}</td>
                <td className="py-1 text-right num">
                  ${o.est_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </td>
                <td className="py-1 text-ink-mute">{o.bucket}</td>
                <td
                  className={`py-1 ${
                    o.verdict === 'allowed' ? 'text-accent' : 'text-warn'
                  }`}
                  title={o.verdict_reason}
                >
                  {o.verdict}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {proposal.total_playbook_rejections > 0 && (
        <div className="text-xs text-warn mt-2">
          {proposal.total_playbook_rejections} order
          {proposal.total_playbook_rejections === 1 ? '' : 's'} rejected by
          Playbook
        </div>
      )}
    </div>
  );
}
